// Privacy and Compliance Basics - canonical legal-literacy articles for the Help Center.

import {
  BULLET_VARIANTS,
  FAQ_BLOCK_TYPES,
  FAQ_SECTION_ICON_TONES,
} from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const PRIVACY_BASICS_TOPIC = {
  id: 'privacy-basics',
  title: 'Privacy and Compliance Basics',
  icon: 'shield-check',
  color: 'positive',
  fill: 'positive-light',
  description:
    'What cookies, consent, and the main privacy rules mean - including GDPR, ePrivacy, and CCPA - and whether you need a banner.',
  sections: [
    {
      title: 'Cookies and tracking',
      items: [
        { articleId: 'what-is-a-cookie', icon: 'cookie' },
        { articleId: 'cookie-categories', icon: 'list-todo' },
      ],
    },
    {
      title: 'Consent and European rules',
      items: [
        { articleId: 'what-is-gdpr', icon: 'shield' },
        { articleId: 'eprivacy-cookie-law', icon: 'file-text' },
      ],
    },
    {
      title: 'US privacy and Google',
      items: [
        { articleId: 'what-is-ccpa', icon: 'building' },
        { articleId: 'what-is-google-consent-mode', icon: 'sliders-horizontal' },
      ],
    },
    {
      title: 'Deciding',
      items: [{ articleId: 'do-i-need-a-cookie-banner', icon: 'help-circle' }],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const PRIVACY_BASICS_ARTICLES = {
  'what-is-a-cookie': {
    id: 'what-is-a-cookie',
    title: 'What is a cookie?',
    description:
      'A cookie is one way a site stores or reads data on a visitor’s device; the same storage rules often cover Indexed DB, Local Storage, Session Storage, and pixels.',
    keywords: [
      'HTTP cookie',
      'terminal equipment',
      'storage and access technologies',
      'Indexed DB',
      'indexedDB',
      'localStorage',
      'sessionStorage',
      'Local Storage',
      'Session Storage',
      'tracking pixel',
      'similar technologies',
      'RFC 6265',
      '3rd party',
      'third party cookies',
      'first party cookies',
      'ITP',
      'tracking prevention',
      'CNAME',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'wic-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. This article is orientation, not advice for your site.\nStart with the ICO’s [[guidance on storage and access technologies|https://ico.org.uk/for-organisations/direct-marketing-and-privacy-and-electronic-communications/guidance-on-the-use-of-storage-and-access-technologies/]].',
      },
      {
        id: 'wic-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'A **cookie** is a small piece of data a website asks the visitor’s browser to store and send back later. Sites use cookies to keep a session logged in, remember a language, measure traffic, or help advertisers recognise a visitor.',
      },
      {
        id: 'wic-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'The rules people call “cookie law” are about **storing or reading information on the device** - not the HTTP cookie format by name.',
      },
      {
        id: 'wic-beyond',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Not only cookies',
        icon: 'cookie',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'wic-beyond-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A scan can also find **Indexed DB**, **Local Storage**, and **Session Storage** - and the same analysis often covers **tracking pixels** when they store or read an identifier. Those are still storage or access on the device, even though they are not HTTP cookies.',
          },
          {
            id: 'wic-beyond-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '[[The ePrivacy cookie law|eprivacy-cookie-law]] is the storage-consent rule; [[Cookie categories|cookie-categories]] is how Cookie Armor groups them.',
          },
          {
            id: 'wic-beyond-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'What a scan records, and how to override one row: [[When you can trust a scan|run-a-scan]] · [[Override a tracker category|classify-cookies]].',
          },
        ],
      },
      {
        id: 'wic-party',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'First-party vs third-party',
        icon: 'external-link',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'wic-party-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A **first-party** cookie is set by the domain the visitor is viewing. A **third-party** cookie is set by a different domain - typically an analytics, advertising, or social iframe or pixel.',
          },
          {
            id: 'wic-party-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The distinction matters for which company receives the data and how browsers treat it. It does **not**, by itself, decide whether consent is required.',
          },
          {
            id: 'wic-party-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Classify by **category**, not by who set the cookie.',
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'wic-faq-needed',
        question: 'If I only use cookies, do I always need a banner?',
        answer:
          'It depends on where visitors are, what you store, and whether that storage is strictly necessary. [[Do I need a cookie banner?|do-i-need-a-cookie-banner]]',
      },
      {
        id: 'wic-faq-first-party-consent',
        question: 'Do first-party analytics cookies need consent in Europe?',
        answer:
          'Usually yes. First-party does not make analytics strictly necessary. Classify them as Analytics. [[Cookie categories|cookie-categories]]',
      },
    ],
  },

  'cookie-categories': {
    id: 'cookie-categories',
    title: 'Cookie categories',
    description:
      'Cookie Armor classifies trackers as Analytics, Marketing, Preferences, or Necessary; Necessary is a narrow product boundary, and analytics is not Necessary.',
    keywords: [
      'strictly necessary',
      'functional cookies',
      'statistics cookies',
      'advertising cookies',
      'purposes',
      'Unclassified',
      'Google Analytics',
    ],
    lastUpdated: '2026-10-26',
    content: [
      {
        id: 'cc-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'These labels are a product classification, not a finding that a cookie is legally exempt. Cookie Armor does not decide whether storage is strictly necessary for your site.\nSee the ICO’s [[cookies guidance|https://ico.org.uk/for-organisations/direct-marketing-and-privacy-and-electronic-communications/guide-to-pecr/cookies-and-similar-technologies/]].',
      },
      {
        id: 'cc-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Assign every detected tracker one of four categories: **Analytics**, **Marketing**, **Preferences**, or **Necessary**. Choose from what the storage does, not from who set it.',
      },
      {
        id: 'cc-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: '**Unclassified** is not a fifth category - it means no category is assigned yet, and unclassified trackers stay allowed. A script is held only by its classified categories, never by an unclassified cookie it also writes. [[Unclassified cookies|unclassified-cookies]]',
      },
      {
        id: 'cc-table-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'The four categories',
        icon: 'chart-pie',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'cc-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Category', 'Typical use', 'Consent in EU / UK practice'],
            rows: [
              [
                'Necessary',
                'Load balancing, login session, shopping basket, security - storage without which the service the visitor asked for does not work.',
                'Closest to the legal **strictly necessary** exemption. Still describe these cookies. Consent is commonly not required **if** they truly are strictly necessary.',
              ],
              [
                'Preferences',
                'Remember a language, region, or similar choice the visitor made.',
                'Often still needs consent unless the storage is strictly necessary to deliver a service the visitor requested. That classification is yours, not Cookie Armor’s.',
              ],
              [
                'Analytics',
                'Count visits, measure journeys, improve the site.',
                'The ICO treats analytics storage as **not** strictly necessary and expects consent. Do not classify analytics as Necessary.',
              ],
              [
                'Marketing',
                'Advertising, retargeting, conversion tracking for ads.',
                'Requires consent in EU / EEA / UK practice. Advertising cookies are not a strictly necessary exemption.',
              ],
            ],
            caption:
              'Legal outcome depends on what the cookie actually does, not on the label alone.',
          },
          {
            id: 'cc-necessary-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Necessary** is a narrow product boundary: storage without which the requested service does not work - not useful, convenient, or required only by your business model. Measuring traffic for your own purposes is Analytics - including Google Analytics.',
          },
          {
            id: 'cc-necessary-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The label you pick does not change the legal analysis.',
          },
          {
            id: 'cc-necessary-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Override a row’s category in [[Override a tracker category|classify-cookies]]. After classification, [[how blocking works|how-blocking-works]] decides what can run.',
          },
        ],
      },
    ],
  },

  'what-is-gdpr': {
    id: 'what-is-gdpr',
    title: 'What is GDPR?',
    description:
      'GDPR can apply when you are established in the EU or when you offer services to, or monitor, people there - and Cookie Armor maps that prior-consent experience to the EU, EEA, and UK.',
    keywords: [
      'General Data Protection Regulation',
      'data subject',
      'controller',
      'processor',
      'lawful basis',
      'legitimate interest',
      'territorial scope',
      'Article 3',
      'UK GDPR',
      'EEA',
      'freely given',
      'specific',
      'informed',
      'unambiguous',
      'affirmative action',
      'Article 4(11)',
      'Article 7',
      'pre-ticked boxes',
      'implied consent',
      'cookie wall',
      'withdraw consent',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'gdpr-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. Read the [[official GDPR text|https://eur-lex.europa.eu/eli/reg/2016/679/oj]] and take advice for your organisation.',
      },
      {
        id: 'gdpr-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'The **General Data Protection Regulation (GDPR)** is the EU’s main law on personal data. A cookie banner does not replace it - GDPR also covers lawful basis, transparency, rights requests, security, and vendor contracts.',
      },
      {
        id: 'gdpr-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'For cookies, the **storage** rule comes from [[the ePrivacy Directive|eprivacy-cookie-law]]; GDPR still applies to any personal data those cookies generate.',
      },
      {
        id: 'gdpr-who',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When it changes what you configure',
        icon: 'sliders-horizontal',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'gdpr-who-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Two tests decide whether GDPR-style design applies. The EDPB explains them in [[Guidelines 3/2018 on territorial scope|https://www.edpb.europa.eu/our-work-tools/our-documents/guidelines/guidelines-32018-territorial-scope-gdpr-article-3-version_en]]:',
          },
          {
            id: 'gdpr-who-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              '**Establishment.** You process personal data in the context of an organisation established in the EU, even if the servers are elsewhere.',
              '**Targeting or monitoring.** You are outside the EU but offer goods or services to people in the Union, or you **monitor** their behaviour there. Analytics and advertising that profile visitors in the EU commonly count as monitoring. Having no EU office does not, by itself, put you outside GDPR.',
            ],
          },
          {
            id: 'gdpr-who-uk',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The UK has **UK GDPR**, a related but separate regime. Cookie Armor’s **GDPR** regulation covers visitors in the **EU, EEA, and the UK** as one shipped choice.',
          },
          {
            id: 'gdpr-who-uk-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'That is a product mapping, not a claim that EU GDPR and UK GDPR are identical. [[Choose your regulations|choose-your-regulations]]',
          },
        ],
      },
      {
        id: 'gdpr-consent',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What not to treat as consent',
        icon: 'shield-alert',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'gdpr-consent-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When the law requires **consent**, it means a freely given, specific, informed, and unambiguous indication of the person’s wishes, given by a **clear affirmative action**.',
          },
          {
            id: 'gdpr-consent-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'You must be able to demonstrate it, and withdrawal must be as easy as giving it.',
          },
          {
            id: 'gdpr-consent-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The EDPB’s [[Guidelines 05/2020 on consent|https://www.edpb.europa.eu/our-work-tools/our-documents/guidelines/guidelines-052020-consent-under-regulation-2016679_en]] treat the patterns below as invalid.',
          },
          {
            id: 'gdpr-consent-not',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Not valid consent',
            items: [
              {
                id: 'gdpr-not-pretick',
                text: '**Pre-ticked boxes**, silence, or inactivity.',
              },
              {
                id: 'gdpr-not-scroll',
                text: '**Scrolling or continuing to browse** - treated as inactivity, not a choice.',
              },
              {
                id: 'gdpr-not-implied',
                text: '**Implied consent** (“by using this site…”). There is no opt-out consent under this definition.',
              },
              {
                id: 'gdpr-not-wall',
                text: '**Cookie walls** with no genuine choice. National practice on “consent or pay” still differs - do not treat one headline case as a universal rule.',
              },
            ],
          },
        ],
      },
    ],
  },

  'eprivacy-cookie-law': {
    id: 'eprivacy-cookie-law',
    title: 'The ePrivacy cookie law',
    description:
      'ePrivacy requires consent before most storage or access on a visitor’s device; GDPR then governs any personal data that storage produces - and unclassified cookies still run until you classify them.',
    keywords: [
      'ePrivacy Directive',
      'PECR',
      'Article 5(3)',
      'Cookie Directive',
      '2002/58/EC',
      '2009/136/EC',
      'strictly necessary',
      'TTDSG',
      'prior consent',
      'opt-in',
      'pre-consent',
      'cookies before consent',
      'script blocking',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'ep-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. National implementations of the [[ePrivacy Directive|https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32002L0058]] differ.\nThis article is the EU-level storage rule, not every national variant.',
      },
      {
        id: 'ep-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'People say “GDPR requires a cookie banner.” The instrument that requires consent **before** most cookies are set is the **ePrivacy Directive**. GDPR is the general personal-data law. Both can apply at once.',
      },
      {
        id: 'ep-vs-gdpr',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Storage rule vs personal-data rule',
        icon: 'database',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'ep-vs-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              '**ePrivacy** governs storing or accessing information **on the device** (cookies, localStorage, many pixels). Consent is required before non-exempt storage. [[Consolidated text|https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02002L0058-20091219]]',
              '**GDPR** governs **personal data** - identifiers, profiles, and what you do with them after storage. Other lawful bases can exist for processing that is not cookie storage. [[What is GDPR?|what-is-gdpr]]',
              'Where ePrivacy already requires consent for storage, you cannot swap in a different lawful basis for that same storage. The ICO’s [[fact vs fiction note|https://ico.org.uk/about-the-ico/media-centre/news-and-blogs/2025/09/fact-vs-fiction-ico-debunks-myths-on-storage-and-access-technologies/]] covers this.',
            ],
          },
        ],
      },
      {
        id: 'ep-prior',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Prior consent and blocking',
        icon: 'shield',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'ep-prior-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Non-essential cookies and similar tags must not run until the visitor has given valid consent. If Analytics is already firing while the banner is on screen, the choice is theatre.',
          },
          {
            id: 'ep-prior-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor blocks only **classified** tags; **unclassified means allowed**. That fail-open is a **product behaviour**, not a legal exemption.',
          },
          {
            id: 'ep-prior-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '[[Unclassified cookies|unclassified-cookies]] · [[How blocking works|how-blocking-works]]',
          },
          {
            id: 'ep-product-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor does not list ePrivacy as a separate regulation. The shipped **GDPR** banner is the prior-consent experience for the EU, EEA, and UK.',
          },
          {
            id: 'ep-product-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Cookie Notice** discloses cookies and never blocks scripts - that distinction lives in [[Choose your regulations|choose-your-regulations]]. This is an opt-in duty, not a [[CCPA|what-is-ccpa]] duty.',
          },
        ],
      },
    ],
  },

  'what-is-ccpa': {
    id: 'what-is-ccpa',
    title: 'What is CCPA?',
    description:
      'CCPA is an opt-out model for selling or sharing personal information; Cookie Armor maps that regulation to visitors in the United States, and CPRA is an amendment, not a second picker.',
    keywords: [
      'California Consumer Privacy Act',
      'CPRA',
      'California Privacy Rights Act',
      'Do Not Sell',
      'Do Not Sell or Share',
      'sale of personal information',
      'share',
      'cross-context behavioral advertising',
      'Global Privacy Control',
      'GPC',
      'opt-out',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'ccpa-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. Start with the [[CPPA FAQ|https://cppa.ca.gov/faq.html]] and the [[California Attorney General’s CCPA page|https://oag.ca.gov/privacy/ccpa]].',
      },
      {
        id: 'ccpa-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'The **California Consumer Privacy Act (CCPA)** is California’s consumer privacy law. The **California Privacy Rights Act (CPRA)** amended it; people still say “CCPA” for the whole regime.',
      },
      {
        id: 'ccpa-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Cookie Armor ships one **CCPA** regulation for that amended law - CPRA is not a separate picker. Official consolidated text: [[CCPA statute (CPPA)|https://cppa.ca.gov/pdf/20260101_ccpa_statute.pdf]].',
      },
      {
        id: 'ccpa-vs-gdpr',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Opt-out, not opt-in',
        icon: 'circle-minus',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ccpa-vs-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              'Europe’s cookie rule is **opt-in**: most non-essential storage waits for a yes. CCPA’s headline internet duty is **opt-out of sale and sharing** for covered businesses that sell or share personal information.',
              'A European visitor needs consent to store or access on the device, category by category. A US visitor under CCPA needs notice and a way to say **Do Not Sell or Share My Personal Information**.',
              'Honour browser opt-out preference signals such as [[Global Privacy Control (GPC)|https://globalprivacycontrol.org/]]. GPC is an opt-out signal, not European prior consent.',
              '**Sale** and **share** are broader than a cash invoice. Sending personal information to an advertising network for valuable consideration, or for cross-context behavioural advertising, can count. Whether **your** tags meet those definitions is a facts-and-statute question.',
            ],
          },
        ],
      },
      {
        id: 'ccpa-product',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'US-wide product mapping',
        icon: 'globe',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'ccpa-who-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-circle',
            label: 'Product vs statute',
            text: 'The statute is about California consumers and covered businesses.\nCookie Armor’s shipped **CCPA** regulation is offered for **visitors in the United States**.\nUsing that banner for all US traffic is a product coverage choice, not a statement that every US state has adopted the CCPA. [[Choose your regulations|choose-your-regulations]]',
          },
          {
            id: 'ccpa-gpc-product',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'On a CCPA visit, Cookie Armor allows optional categories first. If the visitor’s browser sends [[Global Privacy Control (GPC)|https://globalprivacycontrol.org/]], those optional categories are denied.',
          },
          {
            id: 'ccpa-gpc-product-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'That is product behaviour, not a finding that every business must treat GPC the same way.',
          },
          {
            id: 'ccpa-both',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'One banner behaviour cannot satisfy both opt-in and opt-out. Show each visitor the regime that matches where they are.',
          },
          {
            id: 'ccpa-both-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'CCPA requires notice and, if you sell or share, an opt-out path - not a copy of the European prior-consent banner.',
          },
          {
            id: 'ccpa-both-3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Whether a banner is the right control is a design choice on top of those duties. [[Do I need a cookie banner?|do-i-need-a-cookie-banner]]',
          },
        ],
      },
    ],
  },

  'what-is-google-consent-mode': {
    id: 'what-is-google-consent-mode',
    title: 'What is Google Consent Mode?',
    description:
      'Google Consent Mode (GCM) tells Google tags how the visitor answered your banner so those tags can adjust measurement and ads; it is not a law, a banner, or a script blocker.',
    keywords: [
      'GCM',
      'Consent Mode v2',
      'EU User Consent Policy',
      'EU UCP',
      'Google tags',
      'conversion modeling',
      'conversion modelling',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'gcm-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. Google Consent Mode is a **Google product mechanism and policy**, not a statute. It does not replace GDPR, ePrivacy, or CCPA.',
      },
      {
        id: 'gcm-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: '**Google Consent Mode (GCM)** is how you tell Google’s own tags what the visitor chose on your banner, so those tags can change their measurement and ads behaviour.',
      },
      {
        id: 'gcm-intro-1b',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Google’s overview: [[Consent mode overview|https://developers.google.com/tag-platform/security/concepts/consent-mode]].',
      },
      {
        id: 'gcm-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'It is not a banner, not a consent collector, and not [[valid consent|what-is-gdpr]]. You still collect a lawful choice; Consent Mode only **communicates** that choice to Google.',
      },
      {
        id: 'gcm-why',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Google’s policy, not a statute',
        icon: 'book-open',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'gcm-why-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Google’s [[EU User Consent Policy|https://www.google.com/about/company/user-consent-policy/]] requires certain disclosures and legally valid consent from visitors in the **European Economic Area, the UK, and Switzerland** when you use Google products that incorporate that policy.',
          },
          {
            id: 'gcm-why-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'For advertisers who measure EEA visitors with Google tags, Google expects you to pass those consent choices through (Consent Mode or an equivalent). [[Updates to consent mode for EEA traffic|https://support.google.com/google-ads/answer/13695607]]',
          },
          {
            id: 'gcm-why-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-circle',
            label: 'Geography',
            text: 'Google’s policy includes **Switzerland**. Cookie Armor’s shipped GDPR regulation covers the **EU, EEA, and UK**. Do not assume the two maps are identical.',
          },
          {
            id: 'gcm-advertisers-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Without passing consent state, Google may limit personalised ads, remarketing, and some measurement for EEA traffic - a **Google product consequence**, not an EU fine.',
          },
          {
            id: 'gcm-advertisers-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When consent is denied, Google describes tags sending less identifying data and using **conversion modeling** to fill gaps. Modeling is Google’s measurement feature, not permission to skip a banner.',
          },
          {
            id: 'gcm-advertisers-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Consent Mode only affects **Google** tags that honour it. It does not block Meta, TikTok, or a custom pixel, and it is not [[how blocking works|how-blocking-works]].',
          },
        ],
      },
      {
        id: 'gcm-product',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Who owns the next step',
        icon: 'user-check',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'gcm-product-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Decide whether Cookie Armor should drive Google tag consent in [[Google Consent Mode setting|google-consent-mode-setting]]. Signal names, category mapping, and `gtag` wiring are in [[Google Consent Mode for developers|google-consent-mode-developers]].',
          },
        ],
      },
    ],
  },

  'do-i-need-a-cookie-banner': {
    id: 'do-i-need-a-cookie-banner',
    title: 'Do I need a cookie banner?',
    description:
      'Decide from who visits, what you store on their devices, and what your tags do with the data - not from a universal yes or no.',
    keywords: [
      'do I need cookies',
      'exempt cookies',
      'rest of world',
      'Cookie Notice',
      'necessary cookies only',
      'privacy notice',
      'cookie notice',
      'Article 13',
      'Article 14',
      'transparency',
      'Privacy Policy URL',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'need-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor is not a law firm and does not provide or host legal pages for your website. This article is a decision process, not a yes or no for your site.',
      },
      {
        id: 'need-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'There is no single worldwide rule that every website must show a cookie banner. Work through **who visits**, **what you store or access on their device**, and **what you do with that information**.',
      },
      {
        id: 'need-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'If you are unsure after that, take advice - do not skip the banner or copy a competitor’s.',
      },
      {
        id: 'need-steps',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Walk through these questions',
        icon: 'help-circle',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'need-steps-block',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'need-step-where',
                title: 'Where are your visitors?',
                text: 'People in the EU, EEA, or UK bring [[ePrivacy cookie rules|eprivacy-cookie-law]] and [[GDPR|what-is-gdpr]] (or UK GDPR). Visitors in the United States may bring [[CCPA|what-is-ccpa]] if you are a covered business that sells or shares personal information. A global site often needs more than one experience. Pick shipped regulations in [[Choose your regulations|choose-your-regulations]].',
              },
              {
                id: 'need-step-what',
                title: 'What do you store or access on the device?',
                text: 'Inventory cookies, localStorage, pixels, and similar tags - not only the ones you remember adding. If the only storage is strictly necessary to deliver the page or service the visitor asked for, many European readings still require **information** but not a consent banner. The moment you add Analytics or Marketing storage, prior consent is the usual European requirement. [[Cookie categories|cookie-categories]]',
              },
              {
                id: 'need-step-data',
                title: 'What do you do with the data?',
                text: 'Measuring journeys, building ads audiences, or sending identifiers to other companies is a different analysis from keeping a shopping basket. Under CCPA, disclosing personal information to an ad network can be a **sale** or **share** even when you never thought of it as selling a list.',
              },
              {
                id: 'need-step-google',
                title: 'Do you run Google tags?',
                text: 'If you use Google Ads, Google Analytics, or related tags for visitors in the EEA, UK, or Switzerland, Google’s [[EU User Consent Policy|https://www.google.com/about/company/user-consent-policy/]] sits on top of the law. Passing consent with [[Google Consent Mode|what-is-google-consent-mode]] is still not a reason to skip the legal analysis.',
              },
            ],
          },
        ],
      },
      {
        id: 'need-checklist',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Information-only vs consent',
        icon: 'info',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'need-check-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'lightbulb',
            label: 'Information-only',
            text: 'If you truly have no non-essential storage and you are not selling or sharing personal information, a consent banner may be unnecessary in many readings - you may still need a clear cookie or privacy explanation. Confirm it against a real inventory, not against hope.',
          },
          {
            id: 'need-policies-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A **privacy policy** explains how you process personal data. A **cookie notice** (or cookie policy) explains what you store or access on the device, why, who sets it, and how to change the choice.',
          },
          {
            id: 'need-policies-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'You can publish one page or two; the duties stay distinct. Cookie Armor does not draft, store, or host either document.',
          },
          {
            id: 'need-check',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Signals that you likely need a control',
            items: [
              {
                id: 'need-check-eu',
                text: 'Visitors in the EU, EEA, or UK **and** you use Analytics or Marketing cookies (or similar storage) - usually a **consent** banner.',
              },
              {
                id: 'need-check-ca',
                text: 'You are a CCPA “business” **and** you sell or share personal information, including via advertising tags - usually an **opt-out** control.',
              },
              {
                id: 'need-check-google',
                text: 'You load Google tags for visitors in regions covered by Google’s EU User Consent Policy.',
              },
              {
                id: 'need-check-third',
                text: 'Third-party iframes and pixels (ads, social, video) set their own cookies when the page loads.',
              },
            ],
          },
          {
            id: 'need-product-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor will not tell you that you are legally required to show a banner. It applies the experiences you select. After you decide, [[choose your regulations|choose-your-regulations]] - including the difference between Cookie Notice and a GDPR banner.',
          },
        ],
      },
    ],
  },
}
